import { Pencil, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { TableCard } from "@/components/shared/TableCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { serverMessage } from "@/lib/apiError";
import { professionIcon } from "@/lib/profession-icons";
import {
  createProfession,
  listProfessionsForAdmin,
  listVerificationStrategies,
  updateProfession,
  type ProfessionAdminRow,
} from "@/services/platformProfessionsService";

import { ProfessionFormDialog } from "./profession-form-dialog";
import {
  canSubmit,
  emptyForm,
  formFrom,
  REGISTER_BACKED_STRATEGY,
  type FormState,
} from "./profession-form-types";

const QUERY_KEY = ["platform", "professions"] as const;
const STRATEGY_KEY = ["platform", "profession-verification-strategies"] as const;

/**
 * The professions this platform has, and how to add one (spec 8a948f97).
 *
 * NOTHING HERE NAMES A PROFESSION — not the list, not the form, not the
 * verification choices, which are fetched because a hardcoded list would drift
 * the moment a fourth strategy shipped and could offer one that does not
 * exist. The only strategy name in this screen's code names a VERIFICATION,
 * not a profession, which is the distinction decision a78bb4f turns on.
 *
 * THE DANGEROUS FIELDS HAVE NO CONTROL AT ALL. `deliveryModel` has none
 * because `rfq_and_catalog` admits a profession to two frozen 2,900-line
 * catalog services; `commissionPermitted` has none because whether a
 * profession may legally be charged is a fact about its code of ethics rather
 * than a setting (spec residue RES-2).
 */
export function ProfessionsPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ProfessionAdminRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm(REGISTER_BACKED_STRATEGY));

  const professionsQuery = useQuery({ queryKey: QUERY_KEY, queryFn: listProfessionsForAdmin });
  const strategiesQuery = useQuery({ queryKey: STRATEGY_KEY, queryFn: listVerificationStrategies });
  const strategies = useMemo(() => strategiesQuery.data ?? [], [strategiesQuery.data]);

  const close = (): void => {
    setAdding(false);
    setEditing(null);
  };

  const saveMutation = useMutation({
    mutationFn: async (): Promise<unknown> => {
      const registerBacked = form.verificationStrategies.includes(REGISTER_BACKED_STRATEGY);
      const registerTypeCode = registerBacked ? form.registerTypeCode || null : null;

      if (editing) {
        return updateProfession(editing.slug, {
          displayName: form.displayName,
          nounLabel: form.nounLabel,
          nounInSentence: form.nounInSentence,
          consumerHint: form.consumerHint.trim() || null,
          scopeExamples: form.scopeExamples.trim() || null,
          iconKey: form.iconKey || null,
          verificationStrategies: form.verificationStrategies,
          registerTypeCode,
          sortOrder: Number(form.sortOrder) || 0,
          isActive: form.isActive,
          triageEligible: form.triageEligible,
        });
      }

      const credentials = form.credentials
        .map((c) => ({
          prfId: Number(c.prfId),
          // The table's CHECK is both-or-neither, so an empty attribute must
          // send null for BOTH halves rather than a kind with nothing in it.
          attributeKind: c.attributeValue.trim() ? "language" : null,
          attributeValue: c.attributeValue.trim() || null,
        }))
        .filter((c) => Number.isFinite(c.prfId) && c.prfId > 0);

      return createProfession({
        slug: form.slug,
        displayName: form.displayName,
        nounLabel: form.nounLabel,
        nounInSentence: form.nounInSentence,
        consumerHint: form.consumerHint.trim() || null,
        scopeExamples: form.scopeExamples.trim() || null,
        iconKey: form.iconKey || null,
        verificationStrategies: form.verificationStrategies,
        registerTypeCode,
        credentials: registerBacked ? credentials : undefined,
        sortOrder: Number(form.sortOrder) || 0,
        isActive: form.isActive,
        triageEligible: form.triageEligible,
      });
    },
    onSuccess: async () => {
      // Awaited before closing: the list behind the dialog must show the new
      // row, not the state it held when the dialog opened.
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(t("platform.professions.saved"));
      close();
    },
    onError: (error: unknown) => {
      /*
        THE SERVER'S REASON, through the shared helper. Every refusal this
        endpoint makes is specific and actionable — an unimplemented strategy
        lists the ones available, an over-long slug names the real limit — and
        a generic toast throws away the only part that tells an admin what to
        change.
      */
      toast.error(serverMessage(error) ?? t("platform.professions.saveError"));
    },
  });

  const openAdd = (): void => {
    setForm(emptyForm(strategies[0] ?? REGISTER_BACKED_STRATEGY));
    setEditing(null);
    setAdding(true);
  };

  const openEdit = (row: ProfessionAdminRow): void => {
    setForm(formFrom(row));
    setAdding(false);
    setEditing(row);
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]): void =>
    setForm((prev) => ({ ...prev, [key]: value }));

  if (professionsQuery.isLoading) return <LoadingLogo />;

  const rows = professionsQuery.data ?? [];

  return (
    <div className="space-y-6 p-4 md:p-8">
      <PageHeader
        title={t("platform.professions.title")}
        description={t("platform.professions.subtitle")}
        actions={
          <Button type="button" onClick={openAdd}>
            <Plus className="mr-2 h-4 w-4" />
            {t("platform.professions.add")}
          </Button>
        }
      />

      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("platform.professions.columns.profession")}</TableHead>
              <TableHead>{t("platform.professions.columns.verification")}</TableHead>
              <TableHead>{t("platform.professions.columns.fee")}</TableHead>
              <TableHead>{t("platform.professions.columns.state")}</TableHead>
              <TableHead className="text-right">
                {t("platform.professions.columns.actions")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <ProfessionRow key={row.slug} row={row} onEdit={() => openEdit(row)} />
            ))}
          </TableBody>
        </Table>
      </TableCard>

      <ProfessionFormDialog
        open={adding || editing !== null}
        isEditing={editing !== null}
        editingName={editing?.displayName ?? null}
        form={form}
        strategies={strategies}
        saving={saveMutation.isPending}
        canSave={canSubmit(form, editing !== null)}
        onChange={setField}
        onClose={close}
        onSave={() => saveMutation.mutate()}
      />
    </div>
  );
}

/** One registry row. Module-level, so the page is a page and not a renderer. */
function ProfessionRow({
  row,
  onEdit,
}: {
  row: ProfessionAdminRow;
  onEdit: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const Icon = professionIcon(row.iconKey);
  /*
    Defensive, and earned: while the API deploy lagged the portal, this table
    was served the OLDER four-field profession shape, `credentials` came back
    undefined, and reading .length threw inside the row — which is also what
    made the edit button appear to do nothing.
  */
  const credentials = row.credentials ?? [];

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <div>
            <div className="font-medium text-foreground">{row.displayName}</div>
            <div className="text-xs text-muted-foreground">{row.slug}</div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col items-start gap-1">
          {/*
            The strategy is NAMED for a human, not printed raw. "poder_judicial"
            is an internal key; what an admin needs to know is which register
            attests this profession, and whether anyone can be verified at all.
          */}
          {/* One badge per verification, because all of them must pass. */}
          <div className="flex flex-wrap gap-1">
            {(row.verificationStrategies ?? []).map((strategy) => (
              <Badge key={strategy} variant="secondary">
                {t(`platform.professions.strategies.${strategy}`, { defaultValue: strategy })}
              </Badge>
            ))}
          </div>
          {row.registerTypeCode ? (
            <span className="text-xs text-muted-foreground">
              {t("platform.professions.registerFamily", { code: row.registerTypeCode })}
              {credentials.length > 0
                ? ` · ${t("platform.professions.credentialCount", {
                    count: credentials.length,
                  })}`
                : ""}
            </span>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        {/*
          NULL and 0 are different facts and must not look alike. A profession
          with no quote_fee_rate row earns the platform 0% on all its work with
          nothing anywhere reporting it, and rendering that as "0%" is how an
          unconfigured profession passes for a deliberate one.
        */}
        {row.consumerFeePercent === null ? (
          <span className="text-sm text-destructive">
            {t("platform.professions.noFeeRate")}
          </span>
        ) : (
          <span className="text-sm text-foreground">{row.consumerFeePercent}%</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant={row.isActive ? "default" : "outline"}>
            {row.isActive ? t("platform.professions.active") : t("platform.professions.inactive")}
          </Badge>
          {/*
            Shown only when NOT routable, because that is the surprising state:
            a profession can be live for its practitioners while the intake has
            not been taught to route to it, and nothing else says so.
          */}
          {!row.triageEligible ? (
            <Badge variant="outline">{t("platform.professions.notTriaged")}</Badge>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onEdit}
          aria-label={t("platform.professions.edit")}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}
